export const sharedTypeMap = Object.freeze({
  sharedWorker: 1,
  broadcastChannel: 2,
  localStorage: 3
})

export class Sharer extends EventTarget {
  name = null
  type = null // sharedTypeMap
  localStorageSharer = null
  broadcastChannelSharer = null
  sharedWorkerSharer = null

  constructor (name, supportType = [sharedTypeMap.sharedWorker, sharedTypeMap.broadcastChannel, sharedTypeMap.localStorage]) {
    super()
    this.name = name
    if (typeof SharedWorker !== 'undefined' && supportType.includes(sharedTypeMap.sharedWorker)) {
      this.type = sharedTypeMap.sharedWorker

      this.sharedWorkerSharer = new SharedWorkerSharer(this.name)
      this.sharedWorkerSharer.addEventListener('updateData', (e) => {
        this.dispatchEvent(new CustomEvent('updateData', e))
      })
    } else if (typeof BroadcastChannel !== 'undefined' && supportType.includes(sharedTypeMap.broadcastChannel)) {
      this.type = sharedTypeMap.broadcastChannel

      this.broadcastChannelSharer = new BroadcastChannelSharer(this.name)
      this.broadcastChannelSharer.addEventListener('updateData', (e) => {
        this.dispatchEvent(new CustomEvent('updateData', e))
      })
      this.broadcastChannelSharer.addEventListener('pageJoin', (e) => {
        this.dispatchEvent(new CustomEvent('pageJoin', e))
      })
      this.broadcastChannelSharer.addEventListener('updateLeader', (e) => {
        this.dispatchEvent(new CustomEvent('updateLeader', e))
      })
    } else if (supportType.includes(sharedTypeMap.localStorage)) {
      this.type = sharedTypeMap.localStorage

      this.localStorageSharer = new LocalStorageSharer(this.name)
      this.localStorageSharer.addEventListener('updateLeader', (e) => {
        this.dispatchEvent(new CustomEvent('updateLeader', e))
      })
      this.localStorageSharer.addEventListener('updateData', (e) => {
        this.dispatchEvent(new CustomEvent('updateData', e))
      })
    }
  }

  postMessage (key, message) {
    if (key === 'leave') {
      switch (this.type) {
        case sharedTypeMap.localStorage:
          if (this.localStorageSharer) this.localStorageSharer.leave()
          break
        case sharedTypeMap.broadcastChannel:
          if (this.broadcastChannelSharer) this.broadcastChannelSharer.leave()
          break
      }
    } else if (key === 'syncData') {
      switch (this.type) {
        case sharedTypeMap.localStorage:
          if (this.localStorageSharer) this.localStorageSharer.syncData(message)
          break
        case sharedTypeMap.broadcastChannel:
          if (this.broadcastChannelSharer) this.broadcastChannelSharer.syncData(message)
          break
      }
    } else {
      switch (this.type) {
        case sharedTypeMap.sharedWorker:
          if (this.sharedWorkerSharer) this.sharedWorkerSharer.postMessage(key, message)
          break
      }
    }
  }
}

class SharedWorkerSharer extends EventTarget {
  worker = null
  constructor (name) {
    super()
    const sharedWorkerUrl = `/js/${name}.js?env=${process.env.VUE_APP_ENV}&v=${process.env.VUE_APP_VERSION}`
    this.worker = new SharedWorker(`${process.env.NODE_ENV === 'development' ? '' : '/mgmt'}${sharedWorkerUrl}`)

    this.worker.port.onmessage = this.handelMessageEvent.bind(this)
  }

  /**
   * shared message 監聽事件，僅針對 leader / data 的異動進行處理
   * @param e 異動 event
   */
  handelMessageEvent (e) {
    const { data } = e
    switch (data.type) {
      case 'updateData':
        this.dispatchEvent(new CustomEvent('updateData', {
          detail: {
            data: data.data
          }
        }))
        break
    }
  }

  /**
   * 與 SharedWorker 通訊
   */
  postMessage (key, message) {
    this.worker.port.postMessage({ type: key, data: message })
  }
}

class BroadcastChannelSharer extends EventTarget {
  name = null
  leader = null // 主控分頁
  pageName = null
  setLeaderTimeout = null
  broadcastChannel = null
  leaderHeartbeat = null

  constructor (name) {
    super()
    this.name = name
    this.pageName = generateUUID()
    this.broadcastChannel = new BroadcastChannel(name)

    this.broadcastChannel.addEventListener('message', this.handelMessageEvent.bind(this))
    this.join()
    setTimeout(() => {
      this.heartbeat()
    }, 1000)

    setInterval(() => {
      this.heartbeat()
    }, 10000)
  }

  /**
   * 加入時發送消息，並等待隨機秒數，若無等到 leader 回應則代表當前沒 leader
   */
  join () {
    this.broadcastChannel.postMessage({
      key: 'join'
    })
    this.setLeaderTimeout = setTimeout(() => {
      if (!this.leader) {
        this.createLeader()
      }
      this.setLeaderTimeout = null
    }, randomTime())
  }

  /**
   * 頁面關閉時呼叫，僅有 leader 才需要通知所有分頁，為了重新選舉 leader
   */
  leave () {
    // leader 退出
    if (this.leader === this.pageName) {
      this.leader = ''
      this.broadcastChannel.postMessage({
        key: 'setLeader',
        data: {
          leader: ''
        }
      })
    }
  }

  /**
   * 同步資料，僅有 leader 可以進行更新
   * @param data 更新的資料
   */
  syncData (data) {
    if (this.pageName === this.leader) {
      this.broadcastChannel.postMessage({
        key: 'syncData',
        data: {
          leader: this.pageName,
          data
        }
      })
    }
  }

  /**
   * 由自己擔任 leader
   */
  createLeader () {
    this.broadcastChannel.postMessage({
      key: 'setLeader',
      data: {
        leader: this.pageName
      }
    })
    this.broadcastChannel.postMessage({
      key: 'channelHeartbeat',
      data: {
        heartbeat: new Date().getTime()
      }
    })
    this.leader = this.pageName
    this.updateLeader()
  }

  /**
   * heartbeat
   */
  heartbeat () {
    if (this.leader === this.pageName) {
      this.broadcastChannel.postMessage({
        key: 'channelHeartbeat',
        data: {
          heartbeat: new Date().getTime()
        }
      })
    } else {
      if (!this.leaderHeartbeat || new Date().getTime() - Number(this.leaderHeartbeat) > 30000) {
        this.setLeaderTimeout = setTimeout(() => {
          this.createLeader()
          this.setLeaderTimeout = null
        }, randomTime())
      }
    }
  }

  /**
   * BroadcastChannel message 監聽事件，僅針對 leader / data 的異動進行處理
   * @param e 異動 event
   */
  handelMessageEvent (e) {
    const { key, data = {} } = e.data || {}
    if (key === 'join' && this.pageName === this.leader) {
      this.broadcastChannel.postMessage({
        key: 'channelHeartbeat',
        data: {
          heartbeat: new Date().getTime()
        }
      })
      this.dispatchEvent(new CustomEvent('pageJoin', {
        detail: {}
      }))
    } else if (key === 'setLeader') {
      this.leader = data.leader
      if (this.setLeaderTimeout) clearTimeout(this.setLeaderTimeout)
      // 原本的 leader 離開，需要重新選擇 leader
      if (data.leader === '') {
        // 隨機倒數生成 leader，必免多分頁搶當 leader
        this.setLeaderTimeout = setTimeout(() => {
          this.createLeader()
          this.setLeaderTimeout = null
        }, randomTime())
      } else {
        this.setLeaderTimeout = null
        this.updateLeader()
      }
      this.updateLeader()
    } else if (key === 'syncData') {
      this.leader = data.leader
      this.dispatchEvent(new CustomEvent('updateData', {
        detail: {
          data: data.data
        }
      }))
    } else if (key === 'channelHeartbeat') {
      this.leaderHeartbeat = data.heartbeat
    }
  }

  /**
   * 當更新 leader 時，需往上傳方送事件同步更新
   */
  updateLeader () {
    this.dispatchEvent(new CustomEvent('updateLeader', {
      detail: {
        leader: this.leader,
        isSelf: this.leader === this.pageName
      }
    }))
  }
}

class LocalStorageSharer extends EventTarget {
  name = null
  leader = null // 主控分頁
  pageName = null
  setLeaderTimeout = null

  constructor (name) {
    super()
    this.name = name
    this.pageName = generateUUID()

    window.addEventListener('storage', this.handelStorageEvent.bind(this))

    setTimeout(() => {
      this.join()
      setTimeout(() => {
        this.heartbeat()
      }, 1000)
    }, 300)

    setInterval(() => {
      this.heartbeat()
    }, 10000)
  }

  /**
   * 加入時確定是否有 leader，若有則同步資料，若無則自己當 leader
   */
  join () {
    const leader = localStorage.getItem(`${this.name}_leader`)
    if (leader) {
      this.leader = leader
      let data = localStorage.getItem(`${this.name}_data`)
      try {
        data = JSON.parse(data)
      } catch (e) {}
      this.dispatchEvent(new CustomEvent('updateData', {
        detail: {
          data
        }
      }))
      this.updateLeader()
    } else {
      this.setLeaderTimeout = setTimeout(() => {
        if (!this.leader) {
          this.createLeader()
        }
        this.setLeaderTimeout = null
      }, randomTime())
    }
  }

  /**
   * 頁面關閉時呼叫，僅有 leader 才需要通知所有分頁，為了重新選舉 leader
   */
  leave () {
    // leader 退出
    if (this.leader === this.pageName) {
      this.leader = ''
      localStorage.setItem(`${this.name}_leader`, '')
    }
  }

  /**
   * 資料更新時呼叫，僅有 leader 可以進行更新
   * @param message 更新的資料
   */
  syncData (message) {
    // leader 才能更新資料
    if (this.leader === this.pageName) {
      let data = message
      if (typeof message === 'object') data = JSON.stringify(message)
      localStorage.setItem(`${this.name}_data`, data)
    }
  }

  /**
   * 由自己擔任 leader
   */
  createLeader () {
    localStorage.setItem(`${this.name}_leader`, this.pageName)
    localStorage.setItem(`${this.name}_leader_heartbeat`, new Date().getTime())
    this.leader = this.pageName
    this.updateLeader()
  }

  /**
   * heartbeat
   */
  heartbeat () {
    if (this.leader === this.pageName) {
      localStorage.setItem(`${this.name}_leader_heartbeat`, new Date().getTime())
    } else {
      const leaderHeartbeat = localStorage.getItem(`${this.name}_leader_heartbeat`)
      if (!leaderHeartbeat || new Date().getTime() - Number(leaderHeartbeat) > 30000) {
        this.setLeaderTimeout = setTimeout(() => {
          this.createLeader()
          this.setLeaderTimeout = null
        }, randomTime())
      }
    }
  }

  /**
   * localStorage 監聽事件，僅針對 leader / data 的異動進行處理
   * @param e 異動 event
   */
  handelStorageEvent (e) {
    const { key, newValue } = e
    // 變更 leader
    if (key === `${this.name}_leader`) {
      // 原本的 leader 關閉
      this.leader = newValue
      if (this.setLeaderTimeout) clearTimeout(this.setLeaderTimeout)
      if (newValue === '') {
        // 隨機倒數生成 leader，必免多分頁搶當 leader
        this.setLeaderTimeout = setTimeout(() => {
          this.createLeader()
          this.setLeaderTimeout = null
        }, randomTime())
      } else {
        this.setLeaderTimeout = null
        this.updateLeader()
      }
    } else if (key === `${this.name}_data`) {
      let data = newValue
      try {
        data = JSON.parse(newValue)
      } catch (e) {}
      this.dispatchEvent(new CustomEvent('updateData', {
        detail: {
          data
        }
      }))
    }
  }

  /**
   * 當更新 leader 時，需往上傳方送事件同步更新
   */
  updateLeader () {
    this.dispatchEvent(new CustomEvent('updateLeader', {
      detail: {
        leader: this.leader,
        isSelf: this.leader === this.pageName
      }
    }))
  }
}


// 生成頁面專屬 UUID
const generateUUID = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID()
  }

  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, c =>
      (c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> c / 4).toString(16)
    )
  }

  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0
    const v = c === 'x' ? r : (r & 0x3 | 0x8)
    return v.toString(16)
  })
}

// random Time 100~500
const randomTime = () => Math.random() * 400 + 100
